import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Wifi, Volume2 } from "lucide-react";
import { previewTTS } from "@/lib/tts";

export default function Settings() {
  const { user, updateSettings, refetch } = useAuth();
  const [settings, setSettings] = useState(user?.settings || {});
  const [saving, setSaving] = useState(false);
  const [proxyTest, setProxyTest] = useState(null);
  const [testingProxy, setTestingProxy] = useState(false);
  const [previewingVoice, setPreviewingVoice] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  const set = (key, value) => setSettings((s) => ({ ...s, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings(settings);
      toast.success("Настройки сохранены");
    } catch (e) {
      toast.error("Не удалось сохранить настройки");
    } finally {
      setSaving(false);
    }
  };

  const handleTestProxy = async () => {
    setTestingProxy(true);
    setProxyTest(null);
    try {
      const { data } = await api.post("/auth/test-proxy", { proxy_url: settings.proxy_url });
      setProxyTest(data);
    } catch (e) {
      setProxyTest({ success: false, error: "Не удалось проверить прокси" });
    } finally {
      setTestingProxy(false);
    }
  };

  const handlePreviewVoice = async () => {
    setPreviewingVoice(true);
    try {
      await previewTTS("Hello world", settings.tts_model || "tts-1-hd", settings.tts_voice || "nova");
    } catch (e) {
      toast.error("Не удалось воспроизвести пример");
    } finally {
      setPreviewingVoice(false);
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    setSavingEmail(true);
    try {
      await api.post("/auth/change-email", { new_email: newEmail, current_password: emailPassword });
      await refetch();
      toast.success("Email изменён");
      setNewEmail("");
      setEmailPassword("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingEmail(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setSavingPassword(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword });
      toast.success("Пароль изменён. Сейчас вы будете перенаправлены на вход.");
      setTimeout(() => {
        window.location.href = "/login";
      }, 1200);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setSavingPassword(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-2xl mx-auto space-y-8" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Настройки</h1>
        <p className="text-sm text-muted-foreground mt-1">Настройте сервис под себя</p>
      </div>

      <section className="space-y-4">
        <h2 className="font-heading font-semibold">Язык</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Родной язык</Label>
            <Input value={settings.native_lang || ""} onChange={(e) => set("native_lang", e.target.value)} data-testid="settings-native-lang-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Изучаемый язык</Label>
            <Input value={settings.target_lang || ""} onChange={(e) => set("target_lang", e.target.value)} data-testid="settings-target-lang-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Часовой пояс</Label>
            <Input value={settings.timezone || ""} onChange={(e) => set("timezone", e.target.value)} data-testid="settings-timezone-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Карточек в день</Label>
            <Input type="number" value={settings.cards_per_day || 20} onChange={(e) => set("cards_per_day", parseInt(e.target.value) || 20)} data-testid="settings-cards-per-day-input" />
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="font-heading font-semibold">Поведение</h2>
        <div className="flex items-center justify-between">
          <Label>Автовоспроизведение</Label>
          <Switch checked={!!settings.autoplay} onCheckedChange={(v) => set("autoplay", v)} data-testid="settings-autoplay-switch" />
        </div>
        <div className="flex items-center justify-between">
          <Label>Автоматическое открытие Review</Label>
          <Switch checked={!!settings.auto_open_review} onCheckedChange={(v) => set("auto_open_review", v)} data-testid="settings-auto-open-review-switch" />
        </div>
        <div className="flex items-center justify-between">
          <Label>Тема</Label>
          <Select value={settings.theme || "light"} onValueChange={(v) => set("theme", v)}>
            <SelectTrigger className="w-36" data-testid="settings-theme-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="light" data-testid="settings-theme-light">Светлая</SelectItem>
              <SelectItem value="dark" data-testid="settings-theme-dark">Тёмная</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="font-heading font-semibold">LLM ключ</h2>
        <RadioGroup value={settings.llm_provider === "own" ? "own" : "server"} onValueChange={(v) => set("llm_provider", v)} data-testid="settings-llm-provider-radio">
          <div className="flex items-center gap-2">
            <RadioGroupItem value="server" id="llm-server" data-testid="settings-llm-server-option" />
            <Label htmlFor="llm-server">Ключ сервиса (по умолчанию)</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="own" id="llm-own" data-testid="settings-llm-own-option" />
            <Label htmlFor="llm-own">Свой ключ OpenAI</Label>
          </div>
        </RadioGroup>
        {settings.llm_provider === "own" && (
          <Input
            type="password"
            placeholder="sk-..."
            value={settings.own_openai_key || ""}
            onChange={(e) => set("own_openai_key", e.target.value)}
            data-testid="settings-own-openai-key-input"
          />
        )}
      </section>

      <section className="space-y-3">
        <h2 className="font-heading font-semibold">Распознавание речи</h2>
        <RadioGroup value={settings.speech_provider || "web_speech"} onValueChange={(v) => set("speech_provider", v)} data-testid="settings-speech-provider-radio">
          <div className="flex items-center gap-2">
            <RadioGroupItem value="web_speech" id="speech-web" data-testid="settings-speech-web-option" />
            <Label htmlFor="speech-web">Web Speech API (браузер)</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="openai" id="speech-openai" data-testid="settings-speech-openai-option" />
            <Label htmlFor="speech-openai">OpenAI Speech</Label>
          </div>
        </RadioGroup>
      </section>

      <section className="space-y-3">
        <h2 className="font-heading font-semibold">Озвучка слов (TTS)</h2>
        <RadioGroup value={settings.tts_provider || "browser"} onValueChange={(v) => set("tts_provider", v)} data-testid="settings-tts-provider-radio">
          <div className="flex items-center gap-2">
            <RadioGroupItem value="browser" id="tts-browser" data-testid="settings-tts-browser-option" />
            <Label htmlFor="tts-browser">Голос браузера (бесплатно)</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="openai" id="tts-openai" data-testid="settings-tts-openai-option" />
            <Label htmlFor="tts-openai">OpenAI TTS (естественное звучание)</Label>
          </div>
        </RadioGroup>
        {settings.tts_provider === "openai" && (
          <div className="grid sm:grid-cols-2 gap-3 pt-1">
            <div className="space-y-1.5">
              <Label>Модель</Label>
              <Select value={settings.tts_model || "tts-1-hd"} onValueChange={(v) => set("tts_model", v)}>
                <SelectTrigger data-testid="settings-tts-model-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tts-1" data-testid="settings-tts-model-tts-1">tts-1 (быстрее, дешевле)</SelectItem>
                  <SelectItem value="tts-1-hd" data-testid="settings-tts-model-tts-1-hd">tts-1-hd (высокое качество)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Голос</Label>
              <Select value={settings.tts_voice || "nova"} onValueChange={(v) => set("tts_voice", v)}>
                <SelectTrigger data-testid="settings-tts-voice-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["alloy", "ash", "coral", "echo", "fable", "nova", "onyx", "sage", "shimmer"].map((v) => (
                    <SelectItem key={v} value={v} data-testid={`settings-tts-voice-${v}`}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        {settings.tts_provider === "openai" && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handlePreviewVoice}
            disabled={previewingVoice}
            data-testid="settings-tts-preview-button"
          >
            {previewingVoice ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Volume2 className="w-4 h-4 mr-1" />}
            Прослушать "Hello world"
          </Button>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="font-heading font-semibold">Прокси для загрузки субтитров</h2>
        <p className="text-sm text-muted-foreground">
          Мобильное/резидентское прокси помогает избежать временных ограничений YouTube на получение субтитров с облачных IP-адресов.
        </p>
        <div className="flex items-center justify-between">
          <Label>Использовать своё прокси</Label>
          <Switch checked={!!settings.proxy_enabled} onCheckedChange={(v) => set("proxy_enabled", v)} data-testid="settings-proxy-enabled-switch" />
        </div>
        <Input
          placeholder="http://user:pass@host:port или socks5://host:port"
          value={settings.proxy_url || ""}
          onChange={(e) => set("proxy_url", e.target.value)}
          data-testid="settings-proxy-url-input"
        />
        <div className="flex items-center gap-3">
          <Button type="button" variant="outline" onClick={handleTestProxy} disabled={testingProxy || !settings.proxy_url} data-testid="settings-test-proxy-button">
            {testingProxy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Wifi className="w-4 h-4 mr-1" />}
            Проверить IP
          </Button>
          {proxyTest && (
            <span
              className={`flex items-center gap-1.5 text-sm ${proxyTest.success ? "text-emerald-600" : "text-destructive"}`}
              data-testid="settings-proxy-test-result"
            >
              {proxyTest.success ? (
                <><CheckCircle2 className="w-4 h-4" /> Прокси работает, IP: {proxyTest.ip}</>
              ) : (
                <><XCircle className="w-4 h-4" /> Ошибка: {proxyTest.error}</>
              )}
            </span>
          )}
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="font-heading font-semibold">Аккаунт</h2>
        <form onSubmit={handleChangeEmail} className="space-y-2 border border-border rounded-lg p-4">
          <p className="text-sm font-medium">Сменить email</p>
          <p className="text-xs text-muted-foreground">Текущий: {user?.email}</p>
          <Input
            type="email"
            placeholder="Новый email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            data-testid="settings-new-email-input"
          />
          <Input
            type="password"
            placeholder="Текущий пароль"
            value={emailPassword}
            onChange={(e) => setEmailPassword(e.target.value)}
            data-testid="settings-email-current-password-input"
          />
          <Button type="submit" size="sm" disabled={savingEmail || !newEmail || !emailPassword} data-testid="settings-change-email-button">
            {savingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : "Сменить email"}
          </Button>
        </form>

        <form onSubmit={handleChangePassword} className="space-y-2 border border-border rounded-lg p-4">
          <p className="text-sm font-medium">Сменить пароль</p>
          <Input
            type="password"
            placeholder="Текущий пароль"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            data-testid="settings-current-password-input"
          />
          <Input
            type="password"
            placeholder="Новый пароль (минимум 6 символов)"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            data-testid="settings-new-password-input"
          />
          <Button type="submit" size="sm" disabled={savingPassword || !currentPassword || !newPassword} data-testid="settings-change-password-button">
            {savingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : "Сменить пароль"}
          </Button>
        </form>
      </section>

      <Button onClick={handleSave} disabled={saving} data-testid="settings-save-button">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Сохранить"}
      </Button>
    </div>
  );
}
